import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { TenantRole } from '@esa/shared';
import { api } from '../../lib/api.js';
import { Badge, Button, Panel, Select } from '../../ui/index.js';
import { useConfigWrite } from './useConfigWrite.js';

/**
 * Configuration → Access → What each role can do (065).
 *
 * A desk decides which role may do the things a desk reasonably differs on —
 * who writes knowledge articles, who merges tickets — within limits the code
 * sets: nothing below its floor, nothing above desk admin, and the settings,
 * access and keys themselves stay with desk admins.
 */

interface Row {
  capability: string;
  label: string;
  description: string;
  floor: TenantRole;
  ceiling: TenantRole;
  defaultRole: TenantRole;
  role: TenantRole;
  changed: { at: string; by: string | null } | null;
}

const ORDER: TenantRole[] = ['requester', 'agent', 'queue_lead', 'tenant_admin'];
const WHO: Record<string, string> = {
  requester: 'Requesters and up',
  agent: 'Agents and up',
  queue_lead: 'Queue leads and up',
  tenant_admin: 'Desk admins only',
};

export function RoleCapabilitiesPanel({ tenant }: { tenant: string }) {
  const write = useConfigWrite(tenant);
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['admin-role-capabilities', tenant],
    queryFn: () => api.get<{ capabilities: Row[] }>(`/api/v1/tenants/${tenant}/admin/role-capabilities`),
  });

  const set = async (r: Row, role: TenantRole | null) => {
    await write.mutateAsync({
      method: 'put',
      path: `/api/v1/tenants/${tenant}/admin/role-capabilities/${r.capability}`,
      body: { role },
      done: role === null ? `${r.label}: back to the default` : `${r.label}: ${WHO[role]!.toLowerCase()}`,
    });
    // What this person may do may have changed with it.
    void qc.invalidateQueries({ queryKey: ['me'] });
  };

  return (
    <Panel
      title="What each role can do"
      subtitle="Change who may do these on this desk. Roles build on each other: a desk admin can always do what an agent can."
    >
      <p className="mb-2 text-2xs leading-relaxed text-ink-tertiary">
        These apply to everyone in a role. The grants above apply to named people and add to them — a knowledge
        reviewer can write, review and publish articles whatever is set here. Nothing here takes a grant away.
      </p>
      {q.isLoading && <p className="text-xs text-ink-tertiary">Loading…</p>}
      {q.error && <p className="text-xs text-danger">These could not be loaded.</p>}
      <ul className="divide-y divide-line-subtle">
        {(q.data?.capabilities ?? []).map((r) => {
          const allowed = ORDER.filter((x) => ORDER.indexOf(x) >= ORDER.indexOf(r.floor) && ORDER.indexOf(x) <= ORDER.indexOf(r.ceiling));
          const isDefault = r.role === r.defaultRole;
          return (
            <li key={r.capability} className="grid gap-2 py-2.5 sm:grid-cols-[1fr_14rem] sm:items-center">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-ink">{r.label}</span>
                  {!isDefault && <Badge tone="info">changed on this desk</Badge>}
                </div>
                {r.description && <p className="text-2xs text-ink-tertiary">{r.description}</p>}
                <p className="text-2xs text-ink-tertiary">
                  Default: {WHO[r.defaultRole]?.toLowerCase()}
                  {r.changed && !isDefault
                    ? ` · changed ${new Date(r.changed.at).toLocaleDateString()}${r.changed.by ? ` by ${r.changed.by}` : ''}`
                    : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Select
                  value={r.role}
                  onValueChange={(v) => void set(r, v as TenantRole)}
                  options={allowed.map((x) => ({ value: x, label: WHO[x]!, ...(x === r.defaultRole ? { hint: 'default' } : {}) }))}
                  aria-label={r.label}
                />
                {!isDefault && (
                  <Button variant="ghost" size="sm" onClick={() => void set(r, null)} disabled={write.isPending}>
                    Reset
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}
